<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import { pendencias as apiPendencias } from '@/api/recursos'
import SeletorDeContexto from '@/components/SeletorDeContexto.vue'
import AvisosFlutuantes from '@/components/ui/AvisosFlutuantes.vue'
import IconeSvg from '@/components/ui/IconeSvg.vue'
import { useAuthStore } from '@/stores/auth'
import { useContextoStore } from '@/stores/contexto'

const auth = useAuthStore()
const contexto = useContextoStore()
const router = useRouter()
const route = useRoute()

const menuAberto = ref(false)

/** Distintivo do menu: quantas pendências existem agora. */
const pendenciasAbertas = ref<number | null>(null)
const pendenciasAtrasadas = ref(0)

/**
 * A espinha da lateral.
 *
 * Cor sólida da empresa quando há uma unidade em foco; listra vertical com as
 * cores de todas quando se está no consolidado. É o sinal que a visão periférica
 * capta antes de qualquer leitura — e o motivo de ele ser uma barra alta e fina
 * na borda em vez de um ponto: barra alta permanece no campo de visão mesmo com
 * os olhos no meio da tela.
 *
 * Redundante com o texto do seletor de propósito. Cor nunca carrega informação
 * sozinha aqui; ela só a reforça.
 */
const espinha = computed(() => {
  if (contexto.empresaAtual) {
    return { background: contexto.empresaAtual.cor }
  }

  const cores = contexto.empresas.map((e) => e.cor)
  if (cores.length === 0) return { background: 'var(--borda)' }

  const fatia = 100 / cores.length
  const paradas = cores
    .map((cor, i) => `${cor} ${i * fatia}% ${(i + 1) * fatia}%`)
    .join(', ')

  return { backgroundImage: `linear-gradient(180deg, ${paradas})` }
})

/* ---------------------------------------------------------------------------
   O ANÚNCIO DE TROCA DE CONTEXTO

   Trocar de empresa muda o que TODA tela mostra. Sem um sinal explícito, a
   pessoa troca, olha para a mesma tabela com números diferentes e não sabe se o
   contexto mudou ou se o dado é outro.

   O anúncio dura pouco e não bloqueia nada. Ele é `aria-live="polite"`: quem usa
   leitor de tela recebe a mesma confirmação, sem ter o foco roubado.
--------------------------------------------------------------------------- */
const anuncio = ref<{ titulo: string; detalhe: string; cor: string } | null>(null)
let temporizador: ReturnType<typeof setTimeout> | undefined

watch(
  () => contexto.versao,
  () => {
    // `versao` começa em zero e só é incrementada por uma troca deliberada: a
    // carga inicial não dispara o anúncio.
    if (contexto.versao === 0) return

    const unidade = contexto.unidadeAtual

    anuncio.value = {
      titulo: contexto.empresaAtual?.nome ?? 'Todas as minhas unidades',
      // Os três focos dizem coisas diferentes. "Empresa inteira" precisa aparecer
      // por escrito: sem isso, focar a empresa e focar uma unidade dela produzem
      // o mesmo anúncio, e a pessoa não sabe em qual dos dois acabou de entrar.
      detalhe: unidade
        ? `${unidade.nome} · ${unidade.cidade}/${unidade.uf}`
        : contexto.empresaAtual
          ? `Empresa inteira · ${contexto.empresaAtual.unidades.length} unidade(s)`
          : 'Consolidado do grupo',
      cor: contexto.empresaAtual?.cor ?? 'var(--texto-suave)',
    }

    clearTimeout(temporizador)
    temporizador = setTimeout(() => (anuncio.value = null), 2600)
  },
)

/* ------------------------------------------------------------------ menu -- */

interface ItemDeMenu {
  nome: string
  rotulo: string
  icone: string
  permissao?: string
  distintivo?: () => number | null
}

const secoes = computed<{ titulo: string | null; itens: ItemDeMenu[] }[]>(() => [
  {
    titulo: null,
    itens: [
      {
        nome: 'painel',
        rotulo: 'Painel',
        icone: 'painel',
        distintivo: () => pendenciasAbertas.value,
      },
      {
        nome: 'contratos',
        rotulo: 'Contratos',
        icone: 'contrato',
        permissao: 'contratos.visualizar',
      },
      {
        nome: 'fornecedores',
        rotulo: 'Fornecedores',
        icone: 'fornecedor',
        permissao: 'fornecedores.visualizar',
      },
    ],
  },
  {
    titulo: 'Administração',
    itens: [
      {
        nome: 'usuarios',
        rotulo: 'Usuários',
        icone: 'pessoas',
        permissao: 'usuarios.visualizar',
      },
      { nome: 'papeis', rotulo: 'Papéis', icone: 'chave', permissao: 'papeis.gerenciar' },
      {
        nome: 'empresas',
        rotulo: 'Empresas e unidades',
        icone: 'empresa',
        permissao: 'empresas.visualizar',
      },
    ],
  },
])

/**
 * Esconder o item que a pessoa não pode abrir evita o clique frustrado; o 403 no
 * servidor evita o acesso. São coisas diferentes e as duas precisam existir.
 */
function visiveis(itens: ItemDeMenu[]): ItemDeMenu[] {
  return itens.filter((i) => !i.permissao || auth.pode(i.permissao))
}

const secoesVisiveis = computed(() =>
  secoes.value
    .map((s) => ({ ...s, itens: visiveis(s.itens) }))
    .filter((s) => s.itens.length > 0),
)

/* ------------------------------------------------------------- ciclo de vida */

// O contexto só existe para usuário autenticado, e precisa ser recarregado a
// cada login — inclusive quando outra pessoa entra na mesma aba.
watch(
  () => auth.user?.id ?? null,
  async (id) => {
    if (id === null) {
      contexto.limpar()
      pendenciasAbertas.value = null
      return
    }
    await contexto.carregar()
    await carregarPendencias()
  },
  { immediate: true },
)

// Trocar de contexto muda o que está vencendo: o distintivo precisa acompanhar.
watch(() => contexto.versao, carregarPendencias)

async function carregarPendencias(): Promise<void> {
  if (!auth.pode('contratos.visualizar')) return

  try {
    const resumo = await apiPendencias.resumo()
    pendenciasAbertas.value = resumo.total
    pendenciasAtrasadas.value = resumo.atrasadas
  } catch {
    // Distintivo é enfeite útil, não informação crítica: se falhar, a tela do
    // painel dirá o que houve. Derrubar o menu por causa dele seria pior.
    pendenciasAbertas.value = null
  }
}

// Navegar fecha o menu no celular — deixá-lo aberto tapa a página que acabou
// de ser aberta.
watch(
  () => route.fullPath,
  () => (menuAberto.value = false),
)

async function sair(): Promise<void> {
  await auth.sair()
  contexto.limpar()
  await router.push({ name: 'login' })
}
</script>

<template>
  <div v-if="auth.user" class="app" :class="{ 'app--menu-aberto': menuAberto }">
    <a class="app__pular" href="#conteudo">Pular para o conteúdo</a>

    <!-- ----------------------------------------------------------- lateral -->
    <aside id="navegacao" class="app__lateral">
      <div class="app__espinha" :style="espinha" aria-hidden="true" />

      <div class="app__lateral-interna">
        <RouterLink :to="{ name: 'painel' }" class="app__marca">
          <span class="app__marca-sigla" aria-hidden="true">GC</span>
          <span class="app__marca-nome">
            Gerência<br />
            <em>de Contratos</em>
          </span>
        </RouterLink>

        <nav class="app__nav" aria-label="Seções do sistema">
          <template v-for="(secao, i) in secoesVisiveis" :key="i">
            <p v-if="secao.titulo" class="app__nav-titulo">{{ secao.titulo }}</p>

            <RouterLink
              v-for="item in secao.itens"
              :key="item.nome"
              :to="{ name: item.nome }"
              class="app__item"
              :aria-current="route.name === item.nome ? 'page' : undefined"
            >
              <IconeSvg :nome="item.icone" :tamanho="17" />
              <span class="app__item-rotulo">{{ item.rotulo }}</span>
              <span
                v-if="item.distintivo?.()"
                class="app__item-conta"
                :class="{ 'app__item-conta--urgente': pendenciasAtrasadas > 0 }"
              >
                {{ item.distintivo() }}
              </span>
            </RouterLink>
          </template>
        </nav>

        <div class="app__rodape">
          <div class="app__pessoa">
            <span class="app__pessoa-nome">{{ auth.user.name }}</span>
            <span class="app__pessoa-papel">
              {{
                auth.user.papel?.nome ??
                (auth.user.acesso_irrestrito ? 'Acesso irrestrito' : 'Sem papel')
              }}
            </span>
          </div>
          <button
            type="button"
            class="botao botao--fantasma app__sair"
            title="Sair do sistema"
            @click="sair"
          >
            <IconeSvg nome="saida" :tamanho="16" rotulo="Sair" />
          </button>
        </div>
      </div>
    </aside>

    <!-- Véu do menu no celular. Fecha ao toque, como manda o padrão de gaveta. -->
    <button
      v-if="menuAberto"
      type="button"
      class="app__veu"
      aria-label="Fechar menu"
      @click="menuAberto = false"
    />

    <!-- ------------------------------------------------------------- topo -->
    <header class="app__topo">
      <button
        type="button"
        class="botao botao--fantasma app__hamburguer"
        :aria-expanded="menuAberto"
        aria-controls="navegacao"
        @click="menuAberto = !menuAberto"
      >
        <IconeSvg nome="menu" :tamanho="20" rotulo="Menu" />
      </button>

      <SeletorDeContexto />
    </header>

    <!-- O anúncio da troca. Fora do fluxo, não empurra nada. -->
    <Transition name="anuncio">
      <div
        v-if="anuncio"
        class="app__anuncio"
        role="status"
        aria-live="polite"
        :style="{ '--anuncio-cor': anuncio.cor }"
      >
        <span class="app__anuncio-marca" aria-hidden="true" />
        <span class="app__anuncio-texto">
          <strong>{{ anuncio.titulo }}</strong>
          <span>{{ anuncio.detalhe }}</span>
        </span>
      </div>
    </Transition>

    <main id="conteudo" class="app__conteudo">
      <slot />
    </main>

    <AvisosFlutuantes />
  </div>

  <!-- Sem usuário: a tela de login desenha a si mesma, sem moldura. -->
  <template v-else>
    <main class="app__solto">
      <slot />
    </main>
    <AvisosFlutuantes />
  </template>
</template>

<style scoped>
.app {
  min-height: 100dvh;
  display: grid;
  grid-template-columns: 15rem 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'lateral topo'
    'lateral conteudo';
}

/* Primeiro alvo do Tab: quem navega por teclado não precisa percorrer o menu
   inteiro a cada página. */
.app__pular {
  position: absolute;
  top: var(--e-2);
  left: var(--e-2);
  z-index: var(--z-aviso);
  padding: var(--e-2) var(--e-3);
  background: var(--superficie);
  border: 1px solid var(--marca);
  border-radius: var(--raio);
  font-size: var(--t-sm);
  font-weight: 500;
  transform: translateY(-200%);
}

.app__pular:focus-visible {
  transform: none;
}

/* ----------------------------------------------------------------- lateral */

.app__lateral {
  grid-area: lateral;
  display: flex;
  background: var(--superficie);
  border-right: 1px solid var(--borda);
  position: sticky;
  top: 0;
  height: 100dvh;
  z-index: var(--z-cabecalho);
}

.app__espinha {
  width: 4px;
  flex: none;
  /* Transição longa e suave: a cor da empresa mudando é o sinal, e um corte
     seco passa despercebido. */
  transition: background-color var(--transicao-marca);
}

.app__lateral-interna {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: var(--e-4) var(--e-3);
  gap: var(--e-5);
  overflow-y: auto;
}

.app__marca {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  padding: var(--e-2);
  text-decoration: none;
  border-radius: var(--raio);
}

.app__marca-sigla {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  flex: none;
  border-radius: var(--raio);
  background: var(--marca);
  color: var(--marca-texto);
  font-family: var(--fonte-titulo);
  font-size: var(--t-sm);
  font-weight: 600;
  letter-spacing: 0.02em;
  transition:
    background-color var(--transicao-marca),
    color var(--transicao-marca);
}

.app__marca-nome {
  font-family: var(--fonte-titulo);
  font-size: var(--t-sm);
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.app__marca-nome em {
  font-style: italic;
  color: var(--texto-suave);
}

.app__nav {
  display: grid;
  gap: 2px;
  align-content: start;
  flex: 1;
}

.app__nav-titulo {
  margin: var(--e-4) 0 var(--e-1);
  padding-inline: var(--e-2);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--texto-fraco);
}

.app__item {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  min-height: 40px;
  padding-inline: var(--e-2);
  border-radius: var(--raio);
  color: var(--texto-suave);
  font-size: var(--t-base);
  font-weight: 500;
  text-decoration: none;
  transition:
    background-color var(--transicao),
    color var(--transicao);
}

.app__item:hover {
  background: var(--superficie-2);
  color: var(--texto);
}

/* O item atual ganha o véu da marca e uma barra na cor da empresa. `aria-current`
   faz o mesmo trabalho para quem usa leitor de tela. */
.app__item[aria-current='page'] {
  background: var(--marca-veu);
  color: var(--texto);
  box-shadow: inset 2px 0 0 var(--marca);
}

.app__item-rotulo {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app__item-conta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  padding-inline: 0.375rem;
  border-radius: 999px;
  background: var(--superficie-3);
  color: var(--texto-suave);
  font-size: var(--t-xs);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* Há coisa atrasada: o distintivo passa a ser de alerta, e não de contagem. */
.app__item-conta--urgente {
  background: var(--erro-fundo);
  color: var(--erro);
}

.app__rodape {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding-top: var(--e-3);
  border-top: 1px solid var(--borda);
}

.app__pessoa {
  display: grid;
  min-width: 0;
  flex: 1;
  line-height: 1.25;
}

.app__pessoa-nome {
  font-size: var(--t-sm);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app__pessoa-papel {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--texto-fraco);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app__sair {
  min-height: 36px;
  padding-inline: var(--e-2);
}

/* -------------------------------------------------------------------- topo */

.app__topo {
  grid-area: topo;
  display: flex;
  align-items: center;
  gap: var(--e-4);
  padding: var(--e-3) clamp(var(--e-4), 3vw, var(--e-6));
  background: color-mix(in srgb, var(--fundo) 85%, transparent);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--borda);
  position: sticky;
  top: 0;
  z-index: calc(var(--z-cabecalho) - 1);
}

.app__hamburguer {
  display: none;
}

/* ---------------------------------------------------------------- anúncio */

.app__anuncio {
  position: fixed;
  top: var(--e-5);
  left: 50%;
  translate: -50% 0;
  z-index: var(--z-aviso);
  display: flex;
  align-items: stretch;
  gap: var(--e-3);
  padding: var(--e-3) var(--e-5) var(--e-3) var(--e-3);
  background: var(--superficie);
  border: 1px solid var(--borda);
  border-radius: var(--raio-lg);
  box-shadow: var(--sombra-3);
  pointer-events: none;
}

.app__anuncio-marca {
  width: 4px;
  border-radius: 2px;
  background: var(--anuncio-cor);
}

.app__anuncio-texto {
  display: grid;
  line-height: 1.3;
}

.app__anuncio-texto strong {
  font-family: var(--fonte-titulo);
  font-size: var(--t-md);
  font-weight: 500;
}

.app__anuncio-texto span {
  font-size: var(--t-xs);
  color: var(--texto-suave);
}

/* Entra de cima (vem do seletor, que está no topo) e sai mais rápido do que
   entrou — saída lenta parece travamento. */
.anuncio-enter-active {
  transition: all 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.anuncio-leave-active {
  transition: all 200ms ease-in;
}

.anuncio-enter-from,
.anuncio-leave-to {
  opacity: 0;
  translate: -50% -0.75rem;
}

/* --------------------------------------------------------------- conteúdo */

.app__conteudo {
  grid-area: conteudo;
  min-width: 0;
  padding: clamp(var(--e-5), 3vw, var(--e-6)) clamp(var(--e-4), 3vw, var(--e-6))
    var(--e-8);
}

.app__solto {
  min-height: 100dvh;
}

.app__veu {
  display: none;
}

/* ------------------------------------------------------------- responsivo */

@media (max-width: 60rem) {
  .app {
    grid-template-columns: 1fr;
    grid-template-areas:
      'topo'
      'conteudo';
  }

  /* A lateral vira gaveta. Fora da tela por `translate`, não por `display`:
     assim ela desliza em vez de piscar, e o conteúdo continua no DOM para o
     leitor de tela encontrar. */
  .app__lateral {
    position: fixed;
    inset: 0 auto 0 0;
    width: min(17rem, 85vw);
    translate: -100% 0;
    transition: translate var(--transicao);
    z-index: var(--z-modal);
    box-shadow: var(--sombra-3);
  }

  .app--menu-aberto .app__lateral {
    translate: 0 0;
  }

  .app__veu {
    display: block;
    position: fixed;
    inset: 0;
    z-index: calc(var(--z-modal) - 1);
    border: 0;
    /* Véu forte o bastante para o fundo parar de competir com a gaveta. */
    background: rgb(0 0 0 / 0.45);
    cursor: pointer;
  }

  .app__hamburguer {
    display: inline-flex;
    min-height: 40px;
  }
}
</style>
